import {
  View,
  Platform,
  PressableProps,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native'
import Animated, {
  interpolateColor,
  useAnimatedStyle,
  interpolate,
} from 'react-native-reanimated'
import cn from '@ui/utils/cn'
import { hex } from '@ui/utils/hex'
import { colors } from '@ui/colors'
import { leadingNone } from '@ui/typography/styles'
import useAnimatedPress from '@ui/animated/useAnimatedPress'
import SafePressable from '@ui/buttons/SafePressable'
import Typography from '@ui/typography'
import { AnimatedBlurView } from '@ui/animated/nativewindReanimated'
import Border from '@ui/effects/Border'

const size = 32
const borderRadius = size / 2
const borderWidth = 1

interface Props extends PressableProps {
  label?: string
  value?: string
  color?: string
  fullWidth?: boolean
}

export default function ChipButton({
  label,
  value,
  color = colors.white,
  disabled,
  onPress,
  fullWidth = false,
}: Props) {
  const {
    animation: animationPress,
    onPressIn,
    onPressOut,
  } = useAnimatedPress({
    duration: 300,
  })
  const both = label && value

  // Keep colors outside of useAnimatedStyle to avoid wrapping in worklet
  const color0 = hex(color, 0)
  const color1 = hex(color, 0.1)

  const stylePressBackground = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      animationPress.value,
      [0, 1],
      [color0, color1]
    ),
  }))

  const stylePressBorder = useAnimatedStyle(() => ({
    opacity: interpolate(animationPress.value, [0, 1], [0.25, 0.5]),
  }))

  return (
    <SafePressable
      disabled={disabled}
      onPress={onPress}
      onPressIn={onPressIn}
      onPressOut={onPressOut}
      style={fullWidth ? { width: '100%' } : undefined}
    >
      <Animated.View
        className="overflow-hidden-fixer flex items-center justify-center overflow-hidden"
        style={[
          stylePressBackground,
          { height: size, borderRadius },
          fullWidth ? { width: '100%' } : undefined,
        ]}
      >
        {Platform.select({
          default: (
            <AnimatedBlurView
              tint="systemThinMaterialDark"
              intensity={100}
              style={[
                StyleSheet.absoluteFill,
                { display: 'flex', borderRadius },
              ]}
            />
          ),
          web: (
            <div
              className="absolute left-0 top-0 h-full w-full"
              style={{ borderRadius, backdropFilter: `blur(${64}px)` }}
            />
          ),
        })}
        <Border
          style={[
            stylePressBorder,
            { borderRadius, borderWidth, borderColor: color },
          ]}
        />
        <View
          className="flex flex-row items-center justify-center"
          style={{
            paddingHorizontal: size / 2,
            gap: size / 2,
            height: size,
            paddingVertical: borderWidth,
          }}
        >
          {label && (
            <Typography
              variant="SM"
              className={cn(leadingNone)}
              style={{ color: hex(color, 0.5) }}
            >
              {label}
            </Typography>
          )}
          {both && (
            <View
              className="h-full"
              style={{ width: 1, backgroundColor: hex(color, 0.25) }}
            />
          )}
          {value && (
            <Typography
              variant="SMB"
              className={cn(leadingNone)}
              style={{ color }}
            >
              {value}
            </Typography>
          )}
        </View>
      </Animated.View>
    </SafePressable>
  )
}
